import { useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { Upload, Copy, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

export default function EditorMedia() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cms/media");
      setItems(data);
    } catch {
      toast.error("Failed to load media");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/cms/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Uploaded ${files.length} file${files.length === 1 ? "" : "s"}`);
      await load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const copyPath = (path) => {
    navigator.clipboard?.writeText(path);
    toast.success("Storage path copied — paste into any image field");
  };

  return (
    <div data-testid="editor-media-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-fredoka text-2xl font-bold text-slate-900">Media library</h2>
          <p className="text-xs text-slate-500 mt-1">All images uploaded from the editor live here. Click <b>Copy path</b> and paste it into any image field.</p>
        </div>
        <label className={`btn-push-green px-4 py-2 text-sm cursor-pointer inline-flex items-center gap-2 ${uploading ? "opacity-60" : ""}`} data-testid="editor-media-upload">
          <Upload size={16}/>
          {uploading ? "Uploading…" : "Upload images"}
          <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} disabled={uploading}/>
        </label>
      </div>

      {loading && <div className="card-clay p-10 text-center text-slate-400">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="card-clay p-10 text-center text-slate-400 flex flex-col items-center gap-2">
          <ImageIcon size={32}/>
          <div>No images yet. Upload your first one above.</div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((m) => {
          const src = `${BACKEND_URL}/api/cms/files/${m.storage_path}`;
          return (
            <div key={m.id} className="card-clay p-2" data-testid={`media-item-${m.id}`}>
              <div className="aspect-square rounded-lg bg-slate-50 overflow-hidden flex items-center justify-center">
                <img src={src} alt={m.alt || m.filename} className="w-full h-full object-cover"/>
              </div>
              <div className="mt-2 text-[10px] text-slate-500 truncate" title={m.filename}>{m.filename}</div>
              <button onClick={() => copyPath(m.storage_path)} className="mt-1 w-full text-[10px] font-semibold text-slate-700 hover:text-green-700 inline-flex items-center justify-center gap-1" data-testid={`media-copy-${m.id}`}>
                <Copy size={10}/> Copy path
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
